// src/pages/Affiliates.js
//
// ═══════════════════════════════════════════════════════════════════════════
//  CHANGE LOG — 2026-09-23 — THE "⟳ Sync Today" BUTTON IS REMOVED.
//
//  WHAT IT LOOKED LIKE IT DID:
//    Sync just this affiliate, for today.
//
//  WHAT IT ACTUALLY DID:
//    The request it sent carried no affiliate ID at all —
//        axios.post(`${API}/revenue/sync`, { date: today })
//    …so the server loaded EVERY active affiliate and synced all of them.
//    This page then picked out the one line matching the card you clicked
//    and showed you only that. So a click on one card quietly ran a full
//    sync of every network, and the message you saw hid it.
//
//  WHY IT IS BEING DELETED INSTEAD OF FIXED:
//    Bulk Sync, on this same page, already does everything this button did
//    and does it safely. Side by side, /revenue/sync had:
//      • no rate limiter        (Bulk Sync: 3 requests/sec, shared)
//      • no retry on 429        (Bulk Sync: 5 tries, 60–300s backoff)
//      • no resume after crash  (Bulk Sync: per-affiliate checkpoints)
//      • no stop button, no live progress
//      • NO CRON PAUSE — Bulk Sync writes /tmp/ipanel_sync_running so the
//        5-minute cron steps aside. This route did not, so the two could
//        hit Everflow at the same moment and fight over the same hourly
//        BigQuery quota.
//      • no admin check on the server (Bulk Sync already has requireRole)
//    And the cron already syncs today every 5 minutes, so a manual
//    "sync today" was repeating work that was happening anyway.
//
//  WHAT TO DO INSTEAD:
//    "Bulk Sync" → tick one affiliate → pick any dates → Start Sync.
//    The "🔌 Test" button still checks a new affiliate's credentials.
//
//  REMOVED IN THIS FILE: the button, its handleSync() function, its
//  `syncing` state, and two bits of on-screen text that pointed at it.
//  Nothing else changed. Bulk Sync itself is untouched.
//
//  PAIRED BACKEND CHANGE: routes/revenue.js loses router.post('/sync').
//  DEPLOY THIS FILE FIRST — once the button is gone, nothing calls it.
// ═══════════════════════════════════════════════════════════════════════════
//
// ═══════════════════════════════════════════════════════════════════════════
//  CHANGE LOG — 2026-09-19 — two bugs, both inside <BulkSyncProgress>.
//  Nothing else in this file changed.
//
//  BUG 1 — THE WRONG NAME FOR THE LOGIN TOKEN
//
//      const tok = localStorage.getItem("token") || "";
//
//    Everywhere else in this app the token is stored under "ipanel_token"
//    (see AuthContext.js). There has never been anything saved under plain
//    "token", so `tok` was always an empty string, the `?token=` parameter
//    was never appended, the SSE connection opened unauthenticated, the
//    backend rejected it, and the live progress stream never once worked.
//
//    You never saw an error because the code quietly fell back to polling
//    every 3 seconds — which looks almost the same, just not live.
//
//  BUG 2 — THE FALLBACK TIMER WAS NEVER TURNED OFF (the real problem)
//
//      es.onerror = () => {
//        es.close();
//        const poll = setInterval(...);                        // starts a timer
//        esRef.current = { close: () => clearInterval(poll) }; // stored in a ref
//      };
//      return () => { try { es.close(); } catch (_) {} };      // closes es only
//
//    The cleanup closed the EventSource — which onerror had ALREADY closed —
//    and never touched the interval. `esRef.current` held the only handle to
//    it and nothing ever called it. So the 3-second timer kept running after
//    you navigated away, for as long as the browser tab stayed open.
//
//    Worse: EventSource calls onerror on every reconnect attempt, so a flaky
//    connection could start SEVERAL timers, all polling the server forever.
//
//    Fixed by keeping the timer id in the effect's own scope and clearing it
//    in the cleanup, plus a guard so onerror can only ever start one.
//
//  ⚠️ NOTE ON THE TOKEN IN THE URL — raised with Aditi 2026-09-19.
//     Now that the token is actually sent, it travels as a query parameter,
//     because the browser's EventSource cannot send an Authorization header.
//     That means an 8-hour JWT lands in the nginx access log. It is already
//     on the P2 list in the audit doc. The proper fix is a short-lived,
//     single-use ticket for the stream rather than the session token — a
//     separate job. If you would rather not send it at all, delete the two
//     `tok` lines below: the polling fallback (now leak-free) still works.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Header  from "../components/Header";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import "../styles/affiliates.css";
import { todayYMD, edtDateTime } from "../utils/edt";

const API = process.env.REACT_APP_API_URL || "https://ipanel.idigitalhub.org/api";

// ADDED 2026-09-19 — one place that names the storage key, so this can never
// drift out of step with AuthContext.js again.
const TOKEN_KEY = "ipanel_token";

/* ─── Add/Edit Modal ─────────────────────── */
function AffiliateModal({ aff, onClose, onSave }) {
  const isEdit = !!aff?.id;
  const [form, setForm] = useState({
    name:         aff?.name         || "",
    type:         aff?.type         || "everflow",
    api_url:      aff?.api_url      || "",
    api_key:      "",
    affiliate_id: aff?.affiliate_id || "",
    username:     aff?.username     || "",
    api_password: "",
    sort_order:   aff?.sort_order   || 0,
    notes:        aff?.notes        || "",
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const isCake = form.type === "cake";
  const handle = (k, v) => setForm(f => ({...f, [k]: v}));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await onSave(form, isEdit ? aff.id : null);
      onClose();
    } catch(err) {
      setError(err.response?.data?.message || "Error saving.");
    } finally { setLoading(false); }
  };

  return (
    <div className="mo" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="mo-box">
        <div className="mo-hd">
          <span className="mo-title">{isEdit?"Edit Affiliate":"Add New Affiliate"}</span>
          <button className="mo-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit} className="mo-body">
          <div className="mo-type-row">
            <label className={`type-card ${form.type==="everflow"?"active":""}`}
              onClick={()=>handle("type","everflow")}>
              <div className="type-icon ef">EF</div>
              <div>
                <div className="type-name">Everflow</div>
                <div className="type-sub">API Key only</div>
              </div>
              <input type="radio" name="type" value="everflow"
                checked={form.type==="everflow"} onChange={()=>{}} style={{display:"none"}}/>
            </label>
            <label className={`type-card ${form.type==="cake"?"active":""}`}
              onClick={()=>handle("type","cake")}>
              <div className="type-icon ck">CK</div>
              <div>
                <div className="type-name">Cake</div>
                <div className="type-sub">Username + Password</div>
              </div>
              <input type="radio" name="type" value="cake"
                checked={form.type==="cake"} onChange={()=>{}} style={{display:"none"}}/>
            </label>
          </div>

          <div className="mo-grid">
            <div className="field">
              <label>Affiliate Name *</label>
              <input required value={form.name}
                onChange={e=>handle("name",e.target.value)}
                placeholder="e.g. Instar, CX3Ads"/>
            </div>
            <div className="field">
              <label>{isCake ? "Base URL *" : "Network Domain *"}</label>
              <input required value={form.api_url}
                onChange={e=>handle("api_url",e.target.value)}
                placeholder={isCake ? "https://publisher.cx3ads.com" : "instar.everflowclient.io"}/>
              {!isCake && <span className="field-hint">Just the domain — e.g. instar.everflowclient.io</span>}
            </div>
            <div className="field">
              <label>API Key *</label>
              <input required={!isEdit} value={form.api_key}
                onChange={e=>handle("api_key",e.target.value)}
                placeholder={isEdit?"Leave blank to keep existing":"Paste API key here"}
                type="password"/>
            </div>
            {isCake && <>
              <div className="field">
                <label>Affiliate ID *</label>
                <input required value={form.affiliate_id}
                  onChange={e=>handle("affiliate_id",e.target.value)}
                  placeholder="e.g. 10898767"/>
              </div>
              <div className="field">
                <label>Username *</label>
                <input required value={form.username}
                  onChange={e=>handle("username",e.target.value)}
                  placeholder="Login username"/>
              </div>
              <div className="field">
                <label>Password</label>
                <input value={form.api_password}
                  onChange={e=>handle("api_password",e.target.value)}
                  placeholder={isEdit?"Leave blank to keep existing":"Login password"}
                  type="password"/>
              </div>
            </>}
            <div className="field">
              <label>Sort Order</label>
              <input type="number" value={form.sort_order}
                onChange={e=>handle("sort_order",parseInt(e.target.value)||0)}
                placeholder="0"/>
            </div>
            <div className="field field-full">
              <label>Notes</label>
              <input value={form.notes}
                onChange={e=>handle("notes",e.target.value)}
                placeholder="Optional notes about this affiliate"/>
            </div>
          </div>

          {error && <div className="mo-error">⚠ {error}</div>}

          <div className="mo-foot">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Add Affiliate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Delete Modal ───────────────────────── */
function DeleteModal({ aff, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="mo" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="mo-box" style={{maxWidth:380}}>
        <div className="mo-hd">
          <span className="mo-title" style={{color:"#DC2626"}}>Remove Affiliate</span>
          <button className="mo-close" onClick={onClose}>✕</button>
        </div>
        <div className="mo-body">
          <p style={{color:"var(--text-primary)",marginBottom:6}}>
            Remove <strong>{aff.name}</strong>?
          </p>
          <p style={{color:"var(--text-secondary)",fontSize:13}}>
            All cached revenue data for this affiliate will also be deleted.
          </p>
          <div className="mo-foot" style={{marginTop:20}}>
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button className="btn-delete" disabled={loading}
              onClick={async()=>{setLoading(true);await onConfirm(aff.id);onClose();}}>
              {loading?"Removing...":"Yes, Remove"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Bulk Sync Modal (configure + confirm) ──── */
function BulkSyncModal({ affiliates, onClose, onStart }) {
  const today = todayYMD();
  const [startDate, setStartDate] = useState(today);
  const [endDate,   setEndDate]   = useState(today);
  const [selectedIds, setSelectedIds] = useState(affiliates.filter(a=>a.status==="active").map(a=>a.id));
  const [mode,      setMode]      = useState("both");
  const [syncOffers,setSyncOffers]= useState(true);
  const [estimate,  setEstimate]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");

  const toggleAff = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  };
  const toggleAll = () => {
    const active = affiliates.filter(a=>a.status==="active");
    if (selectedIds.length === active.length) setSelectedIds([]);
    else setSelectedIds(active.map(a=>a.id));
  };

  // Fetch estimate whenever inputs change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!startDate || !endDate || startDate > endDate || selectedIds.length === 0) {
        setEstimate(null); return;
      }
      try {
        const r = await axios.post(`${API}/bulk-sync/estimate`, {
          startDate, endDate, affiliateIds: selectedIds, mode, syncOffers,
        });
        if (!cancelled) setEstimate(r.data);
      } catch (_) { if (!cancelled) setEstimate(null); }
    })();
    return () => { cancelled = true; };
  }, [startDate, endDate, selectedIds, mode, syncOffers]);

  const fmtTime = (sec) => {
    if (!sec) return "—";
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec/60)}m ${sec%60}s`;
    return `${Math.floor(sec/3600)}h ${Math.floor((sec%3600)/60)}m`;
  };

  const start = async () => {
    setLoading(true); setError("");
    try {
      const r = await axios.post(`${API}/bulk-sync/start`, {
        startDate, endDate, affiliateIds: selectedIds, mode, syncOffers,
      });
      onStart(r.data.jobId);
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || "Could not start sync.");
    } finally { setLoading(false); }
  };

  const days = (() => {
    if (!startDate || !endDate || startDate > endDate) return 0;
    return Math.round((new Date(endDate) - new Date(startDate))/(1000*60*60*24)) + 1;
  })();
  const allActive = affiliates.filter(a=>a.status==="active");
  const allChecked = selectedIds.length === allActive.length && allActive.length > 0;

  return (
    <div className="mo" onClick={e=>e.target===e.currentTarget && !loading && onClose()}>
      <div className="mo-box" style={{maxWidth:560}}>
        <div className="mo-hd">
          <span className="mo-title">⟳ Bulk Sync</span>
          <button className="mo-close" onClick={onClose} disabled={loading}>✕</button>
        </div>
        <div className="mo-body">
          <p className="bs-help">
            Pull revenue (offer + sub-id) and offer details for a date range.
            Background cron will pause while this runs.
          </p>

          {/* Date range */}
          <div className="bs-row">
            <div className="field">
              <label>From</label>
              <input type="date" value={startDate} max={today}
                onChange={e=>setStartDate(e.target.value)}/>
            </div>
            <div className="field">
              <label>To</label>
              <input type="date" value={endDate} max={today} min={startDate}
                onChange={e=>setEndDate(e.target.value)}/>
            </div>
          </div>

          {/* Quick presets */}
          <div className="bs-presets">
            <button type="button" onClick={()=>{setStartDate(today);setEndDate(today);}}>Today</button>
            <button type="button" onClick={()=>{
              const d = new Date(); d.setDate(d.getDate()-1);
              const s = d.toISOString().slice(0,10);
              setStartDate(s); setEndDate(s);
            }}>Yesterday</button>
            <button type="button" onClick={()=>{
              const d = new Date(); d.setDate(d.getDate()-6);
              setStartDate(d.toISOString().slice(0,10)); setEndDate(today);
            }}>Last 7 days</button>
            <button type="button" onClick={()=>{
              const d = new Date(); d.setDate(d.getDate()-29);
              setStartDate(d.toISOString().slice(0,10)); setEndDate(today);
            }}>Last 30 days</button>
            <button type="button" onClick={()=>{
              const n = new Date();
              setStartDate(new Date(n.getFullYear(), n.getMonth(), 1).toISOString().slice(0,10));
              setEndDate(today);
            }}>This month</button>
            <button type="button" onClick={()=>{
              const n = new Date();
              setStartDate(new Date(n.getFullYear(), 0, 1).toISOString().slice(0,10));
              setEndDate(today);
            }}>This year</button>
          </div>

          {/* Affiliate selector */}
          <div className="field" style={{marginTop:14}}>
            <label style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>Affiliates ({selectedIds.length} of {allActive.length} selected)</span>
              <button type="button" className="bs-link" onClick={toggleAll}>
                {allChecked ? "Deselect all" : "Select all"}
              </button>
            </label>
            <div className="bs-aff-list">
              {allActive.map(a => (
                <label key={a.id} className={`bs-aff-pill ${selectedIds.includes(a.id) ? "on" : ""}`}>
                  <input type="checkbox" checked={selectedIds.includes(a.id)} onChange={()=>toggleAff(a.id)}/>
                  <span className={`type-badge ${a.type}`}>{a.type==="everflow"?"EF":"CK"}</span>
                  <span>{a.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Mode + options */}
          <div className="bs-row" style={{marginTop:14}}>
            <div className="field">
              <label>What to sync</label>
              <select value={mode} onChange={e=>setMode(e.target.value)}>
                <option value="both">Revenue (offer + sub-id)</option>
                <option value="offer">Offer revenue only</option>
                <option value="subid">Sub-id revenue only</option>
              </select>
            </div>
            <div className="field">
              <label style={{display:"flex",alignItems:"center",gap:8,marginTop:24,cursor:"pointer"}}>
                <input type="checkbox" checked={syncOffers} onChange={e=>setSyncOffers(e.target.checked)}/>
                Also refresh offer details
              </label>
            </div>
          </div>

          {/* Estimate panel */}
          <div className="bs-estimate">
            <div className="bs-est-row">
              <span>Days</span>
              <strong>{days || "—"}</strong>
            </div>
            <div className="bs-est-row">
              <span>Affiliates</span>
              <strong>{selectedIds.length}</strong>
            </div>
            <div className="bs-est-row">
              <span>Estimated time</span>
              <strong>{estimate ? fmtTime(estimate.estimateSeconds) : "—"}</strong>
            </div>
            {days > 60 && (
              <div className="bs-warning">
                ⚠ Large date ranges can take a while and may run into API rate limits (script auto-retries).
              </div>
            )}
          </div>

          {error && <div className="mo-error">⚠ {error}</div>}

          <div className="mo-foot">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="button" className="btn-save"
              disabled={loading || selectedIds.length===0 || !startDate || !endDate || startDate>endDate}
              onClick={start}>
              {loading ? "Starting…" : "Start Sync"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Bulk Sync Progress Panel (live via SSE) ─── */
function BulkSyncProgress({ jobId, onClose, onComplete }) {
  const [job, setJob]       = useState(null);
  const [stopping, setStopping] = useState(false);
  const esRef = useRef(null);

  useEffect(() => {
    if (!jobId) return;

    // EventSource cannot send an Authorization header, so the backend accepts
    // the same JWT as a query parameter on this one endpoint.
    //
    // ── FIXED 2026-09-19 ────────────────────────────────────────────────
    // Was: localStorage.getItem("token")
    // Nothing is ever stored under that name — AuthContext.js writes
    // "ipanel_token". So this was always "", the ?token= parameter was
    // never added, the stream was rejected, and the code silently fell
    // back to polling. It has never worked since it was written.
    const tok = localStorage.getItem(TOKEN_KEY) || "";
    const url = `${API}/bulk-sync/status/${jobId}${tok ? "?token=" + encodeURIComponent(tok) : ""}`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    // ── ADDED 2026-09-19 ──────────────────────────────────────────────
    // The fallback timer's id, held in the EFFECT'S OWN SCOPE so that the
    // cleanup function below can actually reach it. Previously the only
    // handle lived on esRef.current and nothing ever called it, so the
    // timer outlived the page.
    let pollId = null;

    es.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.job) setJob(d.job);
        if (d.event === "finished" || d.event === "failed") {
          es.close();
          onComplete?.(d.job);
        }
      } catch (_) {}
    };

    es.onerror = () => {
      // Fall back to polling if SSE fails
      es.close();

      // ── ADDED 2026-09-19 ────────────────────────────────────────────
      // EventSource fires onerror on EVERY failed reconnect attempt, not
      // just the first. Without this guard a flaky connection could start
      // several 3-second timers, all polling the server at once, all
      // invisible. One poller, maximum.
      if (pollId !== null) return;

      pollId = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/bulk-sync/current`);
          if (r.data?.job && r.data.job.id === jobId) {
            setJob(r.data.job);
            if (r.data.job.status !== "running") {
              clearInterval(pollId); pollId = null;
              onComplete?.(r.data.job);
            }
          } else {
            clearInterval(pollId); pollId = null;
          }
        } catch (_) {}
      }, 3000);

      esRef.current = {
        close: () => { if (pollId !== null) { clearInterval(pollId); pollId = null; } }
      };
    };

    // ── FIXED 2026-09-19 ──────────────────────────────────────────────
    // Was: return () => { try { es.close(); } catch (_) {} };
    // That closed the EventSource — which onerror had already closed — and
    // never touched the interval. THIS is what left a 3-second timer running
    // after you navigated away, for as long as the tab stayed open. Visit
    // this page five times and you had five of them.
    return () => {
      try { es.close(); } catch (_) {}
      if (pollId !== null) { clearInterval(pollId); pollId = null; }
    };
  }, [jobId, onComplete]);

  const stop = async () => {
    if (!window.confirm("Stop the sync? The worker will finish the current step then halt.")) return;
    setStopping(true);
    try { await axios.post(`${API}/bulk-sync/stop/${jobId}`); }
    catch (_) {}
  };

  if (!job) return (
    <div className="bs-panel">
      <div className="bs-panel-hd"><strong>Bulk Sync</strong> — connecting…</div>
    </div>
  );

  const pct = job.progress.totalSteps
    ? Math.round((job.progress.completedSteps / job.progress.totalSteps) * 100)
    : 0;
  const isRunning = job.status === "running";
  const affs = job.affiliates || [];

  return (
    <div className="bs-panel">
      <div className="bs-panel-hd">
        <div>
          <strong>Bulk Sync</strong>
          <span className={`bs-status ${job.status}`}>
            {job.stopRequested && isRunning ? "stopping…" : job.status}
          </span>
          <span className="bs-range">{job.startDate} → {job.endDate}</span>
        </div>
        <div className="bs-panel-actions">
          {isRunning && !job.stopRequested && (
            <button className="bs-stop" onClick={stop} disabled={stopping}>
              {stopping ? "Stopping…" : "Stop"}
            </button>
          )}
          {!isRunning && (
            <button className="bs-stop" onClick={onClose}>Close</button>
          )}
        </div>
      </div>

      <div className="bs-progress-outer">
        <div className="bs-progress-inner" style={{width: pct + "%"}}/>
      </div>
      <div className="bs-progress-label">
        {job.progress.completedSteps} / {job.progress.totalSteps} steps ({pct}%)
      </div>

      {/* Per-affiliate status */}
      <div className="bs-aff-status">
        {affs.map(a => {
          const p = job.progress.perAffiliate[a.id];
          if (!p) return (
            <div key={a.id} className="bs-aff-row">
              <span className="bs-aff-name">{a.name}</span>
              <span className="bs-aff-pending">waiting…</span>
            </div>
          );
          return (
            <div key={a.id} className={`bs-aff-row ${p.status}`}>
              <span className="bs-aff-name">{a.name}</span>
              <span className="bs-aff-meta">
                {p.status === "running" && p.currentDate && <>📅 {p.currentDate} · </>}
                {p.days || 0} days
                {p.records ? ` · ${p.records} records` : ""}
                {p.errors ? ` · ${p.errors} errors` : ""}
              </span>
              <span className={`bs-aff-state ${p.status}`}>{
                p.status === "completed" ? "✓" :
                p.status === "running"   ? "⏳" :
                p.status === "stopped"   ? "⏹" :
                p.status === "skipped"   ? "↷" : "•"
              }</span>
            </div>
          );
        })}
      </div>

      {/* Offer sync block */}
      {job.syncOffers && Object.keys(job.progress.offerSync || {}).length > 0 && (
        <div className="bs-offers">
          <div className="bs-offers-hd">Offer Lists</div>
          {Object.values(job.progress.offerSync).map((o, i) => (
            <div key={i} className={`bs-aff-row ${o.status}`}>
              <span className="bs-aff-name">{o.name}</span>
              <span className="bs-aff-meta">
                {o.upserted || 0} / {o.fetched || 0}{o.error ? ` · ${o.error}` : ""}
              </span>
              <span className={`bs-aff-state ${o.status}`}>{
                o.status === "completed" ? "✓" :
                o.status === "running"   ? "⏳" :
                o.status === "failed"    ? "✕" : "•"
              }</span>
            </div>
          ))}
        </div>
      )}

      {/* Log tail */}
      <details className="bs-log">
        <summary>Log ({job.log?.length || 0} lines)</summary>
        <pre>{(job.log || []).slice(-50).join("\n")}</pre>
      </details>
    </div>
  );
}

/* ─── Main Page ──────────────────────────── */
export default function Affiliates() {
  const { user } = useAuth();
  const [affiliates, setAffiliates] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [addModal,   setAddModal]   = useState(false);
  const [editAff,    setEditAff]    = useState(null);
  const [deleteAff,  setDeleteAff]  = useState(null);
  const [testing,    setTesting]    = useState(null);
  const [testResult, setTestResult] = useState({});
  // REMOVED 2026-09-23: const [syncing, setSyncing] = useState(null);
  //   This tracked which card's "⟳ Sync Today" button was mid-request.
  //   The button is gone, so nothing sets or reads it any more.
  const [toast,      setToast]      = useState("");
  const [bulkModal,  setBulkModal]  = useState(false);
  const [activeJobId,setActiveJobId]= useState(null);

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(""),3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/affiliates`);
      setAffiliates(r.data.affiliates||[]);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  },[]);

  // Reattach to any running bulk sync job on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/bulk-sync/current`);
        if (r.data?.active && r.data?.job?.id) setActiveJobId(r.data.job.id);
      } catch (_) {}
    })();
  }, []);

  useEffect(()=>{ load(); },[load]);

  const handleSave = async (form, id) => {
    const body = {...form};
    if (!body.api_key)      delete body.api_key;
    if (!body.api_password) delete body.api_password;
    if (id) {
      await axios.put(`${API}/affiliates/${id}`, body);
      showToast(`${form.name} updated successfully.`);
    } else {
      await axios.post(`${API}/affiliates`, body);
      // CHANGED 2026-09-23: said 'Click "Sync" to fetch data.' — no such
      // button any more. Points at Bulk Sync instead.
      showToast(`${form.name} added successfully! Use "Bulk Sync" to fetch its data.`);
    }
    load();
  };

  const handleDelete = async id => {
    await axios.delete(`${API}/affiliates/${id}`);
    showToast("Affiliate removed.");
    load();
  };

  const handleTest = async (aff) => {
    setTesting(aff.id);
    setTestResult(t => ({...t, [aff.id]: null}));
    try {
      const r = await axios.post(`${API}/affiliates/${aff.id}/test`);
      setTestResult(t => ({...t, [aff.id]: {ok:r.data.success, msg: r.data.success?"Connected ✓":r.data.error}}));
    } catch(e) {
      setTestResult(t => ({...t, [aff.id]: {ok:false, msg:"Connection failed"}}));
    } finally { setTesting(null); }
  };

  // ── REMOVED 2026-09-23 — handleSync() ─────────────────────────────────
  //   This was the only thing in the whole app that called
  //   POST /api/revenue/sync. It is deleted; use Bulk Sync instead.
  //
  //   For the record, this is exactly what it used to be, and the line
  //   worth looking at is the third one — the affiliate is never sent:
  //
  //     const handleSync = async (aff) => {
  //       setSyncing(aff.id);
  //       try {
  //         const today = todayYMD();
  //         const r = await axios.post(`${API}/revenue/sync`, { date: today });
  //         const result = r.data.results?.find(x=>x.affiliate===aff.name);
  //         showToast(result?.status==="success"
  //           ? `${aff.name} synced! ${result.records} records fetched.`
  //           : `${aff.name} sync failed: ${result?.error||"Unknown error"}`);
  //         load();
  //       } catch(e) {
  //         showToast(`Sync failed: ${e.response?.data?.message||e.message}`);
  //       } finally { setSyncing(null); }
  //     };
  //
  //   `aff` was used only to colour the message. The work was always global.
  // ───────────────────────────────────────────────────────────────────────

  const onJobComplete = useCallback((job) => {
    if (job?.status === "completed") showToast("Bulk sync completed.");
    else if (job?.status === "stopped") showToast("Bulk sync stopped.");
    else if (job?.status === "failed")  showToast("Bulk sync failed.");
    load();
  }, [load]);

  const TypeBadge = ({type}) => <span className={`type-badge ${type}`}>{type}</span>;
  const StatusDot = ({status}) => <span className={`status-dot ${status}`}>{status}</span>;

  return (
    <div className="layout">
      <Sidebar active="affiliates"/>
      <div className="layout-main">
        <Header title="Affiliate Connections" userName={user?.name}/>
        <div className="content">

          <div className="info-banner">
            <span className="info-icon">💡</span>
            {/* CHANGED 2026-09-23: used to say "then Sync to fetch revenue
                data" — that button no longer exists. Points at Bulk Sync. */}
            <span>Add your affiliate platforms here. Once added, click <strong>Test</strong> to verify the connection, then use <strong>Bulk Sync</strong> to fetch revenue data for any date range.</span>
          </div>

          {/* Active bulk-sync panel (live progress) */}
          {activeJobId && (
            <BulkSyncProgress
              jobId={activeJobId}
              onClose={()=>setActiveJobId(null)}
              onComplete={onJobComplete}
            />
          )}

          {/* Toolbar */}
          <div className="aff-toolbar">
            <div>
              <span className="aff-count">{affiliates.length} affiliate{affiliates.length!==1?"s":""} connected</span>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn-bulk-sync"
                onClick={()=>setBulkModal(true)}
                disabled={!!activeJobId}
                title={activeJobId ? "A sync is already running" : "Bulk sync revenue + offers for a date range"}>
                ⟳ Bulk Sync
              </button>
              <button className="btn-add-aff" onClick={()=>setAddModal(true)}>
                + Add Affiliate
              </button>
            </div>
          </div>

          {loading ? (
            <div className="aff-loading">Loading affiliates...</div>
          ) : affiliates.length === 0 ? (
            <div className="aff-empty">
              <div style={{fontSize:48,marginBottom:12}}>🔗</div>
              <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>No affiliates connected</div>
              <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:16}}>Add your first affiliate to start fetching revenue data</div>
              <button className="btn-add-aff" onClick={()=>setAddModal(true)}>+ Add Your First Affiliate</button>
            </div>
          ) : (
            <div className="aff-grid">
              {affiliates.map(aff => {
                const tr = testResult[aff.id];
                return (
                  <div key={aff.id} className="aff-card">
                    <div className="aff-card-hd">
                      <div className={`aff-icon ${aff.type}`}>
                        {aff.type==="everflow"?"EF":"CK"}
                      </div>
                      <div className="aff-info">
                        <div className="aff-name">{aff.name}</div>
                        <div className="aff-url">{aff.api_url}</div>
                      </div>
                      <div className="aff-badges">
                        <TypeBadge type={aff.type}/>
                        <StatusDot status={aff.status}/>
                      </div>
                    </div>

                    <div className="aff-details">
                      <div className="aff-detail-row">
                        <span className="adr-lbl">API Key</span>
                        <span className="adr-val masked">••••••••••••••••</span>
                      </div>
                      {aff.affiliate_id && (
                        <div className="aff-detail-row">
                          <span className="adr-lbl">Affiliate ID</span>
                          <span className="adr-val">{aff.affiliate_id}</span>
                        </div>
                      )}
                      {aff.username && (
                        <div className="aff-detail-row">
                          <span className="adr-lbl">Username</span>
                          <span className="adr-val">{aff.username}</span>
                        </div>
                      )}
                      <div className="aff-detail-row">
                        <span className="adr-lbl">Last Synced</span>
                        <span className="adr-val">
                          {aff.last_synced
                            ? edtDateTime(aff.last_synced) + " EDT"
                            : <span style={{color:"#EF4444"}}>Never synced</span>}
                        </span>
                      </div>
                      {aff.notes && (
                        <div className="aff-detail-row">
                          <span className="adr-lbl">Notes</span>
                          <span className="adr-val">{aff.notes}</span>
                        </div>
                      )}
                    </div>

                    {tr && (
                      <div className={`test-result ${tr.ok?"ok":"fail"}`}>
                        {tr.ok ? "✅" : "❌"} {tr.msg}
                      </div>
                    )}

                    <div className="aff-actions">
                      <button className="aff-btn test"
                        disabled={testing===aff.id}
                        onClick={()=>handleTest(aff)}>
                        {testing===aff.id ? "Testing..." : "🔌 Test"}
                      </button>
                      {/* REMOVED 2026-09-23 — the "⟳ Sync Today" button.
                          It sent no affiliate ID, so it synced every
                          network on every click. Use Bulk Sync (top of
                          this page) and tick just this affiliate. */}
                      <button className="aff-btn edit" onClick={()=>setEditAff(aff)}>
                        ✏ Edit
                      </button>
                      <button className="aff-btn del" onClick={()=>setDeleteAff(aff)}>
                        🗑
                      </button>
                    </div>
                  </div>
                );
              })}

              <div className="aff-card aff-card-add" onClick={()=>setAddModal(true)}>
                <div className="add-icon">+</div>
                <div className="add-label">Add Affiliate</div>
                <div className="add-sub">Everflow or Cake</div>
              </div>
            </div>
          )}

        </div>
      </div>

      {addModal   && <AffiliateModal aff={null}    onClose={()=>setAddModal(false)}   onSave={handleSave}/>}
      {editAff    && <AffiliateModal aff={editAff} onClose={()=>setEditAff(null)}     onSave={handleSave}/>}
      {deleteAff  && <DeleteModal    aff={deleteAff} onClose={()=>setDeleteAff(null)} onConfirm={handleDelete}/>}
      {bulkModal  && <BulkSyncModal  affiliates={affiliates} onClose={()=>setBulkModal(false)}
                                     onStart={(id)=>setActiveJobId(id)}/>}

      {toast && <div className="toast">✅ {toast}</div>}
    </div>
  );
}
